import React, { useState, useEffect } from 'react';
import { usePageVisible } from '../hooks/usePageVisible';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Circle,
  AccountCircle,
  Settings,
  Logout,
  Menu as MenuIcon,
  PowerSettingsNew,
  Star,
  Notifications as NotificationsIcon,
  DoneAll,
} from '@mui/icons-material';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import { useMediaQuery, useTheme } from '@mui/material';
import { useReadOnly } from '../ReadOnlyContext';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useTradingMode } from '../contexts/TradingModeContext';
import api from '../services/api';
import EmergencyStopDialog from './EmergencyStopDialog';

interface SystemHealth {
  status: string;
  services: Record<string, { status: string; [key: string]: any }>;
}

const statusColor = (status: string): string => {
  switch (status) {
    case 'healthy': return '#4caf50';
    case 'warning': return '#E8A045';
    case 'error': return '#f44336';
    default: return '#9e9e9e';
  }
};

const Navbar: React.FC = () => {
  const pageVisible = usePageVisible();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { toggleMobile } = useSidebar();
  const { readonly } = useReadOnly();
  const { user, logout } = useAuth();
  const { tradingMode, toggleMode, kalshiConnected, isToggling } = useTradingMode();
  const navigate = useNavigate();
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showKalshiDialog, setShowKalshiDialog] = useState(false);
  const [showEmergencyStop, setShowEmergencyStop] = useState(false);
  const [emergencyPositions, setEmergencyPositions] = useState(0);
  const [emergencyAtRisk, setEmergencyAtRisk] = useState(0);
  const menuOpen = Boolean(anchorEl);

  // Notification bell state
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<any[]>([]);

  // Fetch system health for single status dot.
  // /api/v1/system/health is admin-only — polling it for every user produced a
  // console 403 every 60s and a permanently-dead dot. Only admins poll/see it.
  const isAdmin = !!user?.is_admin;
  useEffect(() => {
    if (!isAdmin) return;
    const fetchHealth = async () => {
      try {
        const res = await api.get('/api/v1/system/health');
        setSystemHealth(res.data);
      } catch { /* ignore */ }
    };
    fetchHealth();
    const interval = setInterval(() => { if (!document.hidden) fetchHealth(); }, 60000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin && pageVisible && systemHealth) {
      api.get('/api/v1/system/health').then(res => setSystemHealth(res.data)).catch(() => {});
    }
  }, [pageVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch notification count
  useEffect(() => {
    if (!user) return;
    const fetchCount = async () => {
      try {
        const res = await api.get('/api/v1/notifications/count');
        setUnreadCount(res.data.count || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const interval = setInterval(() => { if (!document.hidden) fetchCount(); }, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const handleOpenNotifications = async (event: React.MouseEvent<HTMLElement>) => {
    setNotifAnchor(event.currentTarget);
    try {
      const res = await api.get('/api/v1/notifications/');
      setNotifications(res.data);
    } catch { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post('/api/v1/notifications/read-all');
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch { /* ignore */ }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleSettings = () => { handleMenuClose(); navigate('/settings'); };
  const handleLogout = () => { handleMenuClose(); logout(); navigate('/login'); };

  const handleEmergencyStopClick = async () => {
    try {
      const res = await api.get('/api/v1/positions/stats/summary?mode=live&trade_source=all');
      setEmergencyPositions(res.data.open_positions || 0);
      setEmergencyAtRisk(res.data.total_cost || 0);
    } catch {
      setEmergencyPositions(0);
      setEmergencyAtRisk(0);
    }
    setShowEmergencyStop(true);
  };

  const handleModeToggle = () => {
    if (tradingMode === 'paper' && !kalshiConnected) {
      setShowKalshiDialog(true);
      return;
    }
    toggleMode();
  };

  // Overall system status: single dot
  const overallStatus = systemHealth?.status || 'loading';

  return (
    <Box
      sx={{
        height: 48,
        display: 'flex',
        alignItems: 'center',
        px: { xs: 1, sm: 2 },
        gap: { xs: 0.75, sm: 1.5 },
        borderBottom: '1px solid #F0E4CE',
        backgroundColor: '#FFFFFF',
        flexShrink: 0,
      }}
    >
      {/* Hamburger — mobile */}
      {isMobile && (
        <IconButton onClick={toggleMobile} size="small" sx={{ color: '#4A3728' }} aria-label="Open navigation">
          <MenuIcon />
        </IconButton>
      )}

      {/* Trading Mode Toggle */}
      {readonly ? (
        <Chip
          icon={<Circle sx={{ fontSize: '10px !important' }} />}
          label="VIEW ONLY"
          size="small"
          sx={{
            backgroundColor: '#E8A04526',
            color: '#E8A045',
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: 0.5,
            border: '1px solid #E8A0454D',
            '& .MuiChip-icon': { color: '#E8A045' },
          }}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Chip
            label={tradingMode === 'paper' ? 'Paper' : 'Live'}
            size="small"
            onClick={handleModeToggle}
            disabled={isToggling}
            sx={{
              fontWeight: 700,
              fontSize: '0.75rem',
              height: 26,
              cursor: 'pointer',
              backgroundColor: tradingMode === 'live' ? '#1D4ED8' : '#3A8C8C',
              color: '#fff',
              '&:hover': { backgroundColor: tradingMode === 'live' ? '#1e40af' : '#2A6666' },
            }}
          />
        </Box>
      )}

      {/* Stop All — live mode */}
      {tradingMode === 'live' && !readonly && (
        <Tooltip title="Stop all trading" arrow>
          <IconButton size="small" onClick={handleEmergencyStopClick} sx={{ color: '#C0392B' }} aria-label="Stop all trading">
            <PowerSettingsNew />
          </IconButton>
        </Tooltip>
      )}

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* System status dot — admin-only (health endpoint requires admin) */}
      {!isMobile && isAdmin && (
        <Tooltip title={overallStatus === 'healthy' ? 'All systems normal' : overallStatus === 'warning' ? 'Partial issue — check settings' : 'System issue'} arrow>
          <Circle sx={{ fontSize: 10, color: statusColor(overallStatus), cursor: 'pointer' }} onClick={() => navigate('/system')} />
        </Tooltip>
      )}

      {/* Notifications */}
      <IconButton size="small" onClick={handleOpenNotifications} aria-label="Notifications" sx={{ color: '#7A5F4E' }}>
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon sx={{ fontSize: 20 }} />
        </Badge>
      </IconButton>
      <Popover
        open={Boolean(notifAnchor)}
        anchorEl={notifAnchor}
        onClose={() => setNotifAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: 320, maxHeight: 400, overflow: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderBottom: '1px solid #eee' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Notifications</Typography>
            {unreadCount > 0 && (
              <Button size="small" startIcon={<DoneAll sx={{ fontSize: 14 }} />} onClick={handleMarkAllRead} sx={{ fontSize: 11 }}>
                Mark all read
              </Button>
            )}
          </Box>
          {notifications.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No notifications
            </Typography>
          ) : (
            notifications.slice(0, 15).map((n: any) => (
              <Box
                key={n.id}
                sx={{
                  p: 1.5, borderBottom: '1px solid #f5f5f5', cursor: 'pointer',
                  bgcolor: n.is_read ? 'transparent' : 'rgba(232,160,69,0.06)',
                  '&:hover': { bgcolor: '#f9f6f1' },
                }}
                onClick={() => { setNotifAnchor(null); if (n.link_url) navigate(n.link_url); }}
              >
                <Typography variant="body2" sx={{ fontWeight: n.is_read ? 400 : 600, fontSize: 13 }}>
                  {n.title}
                </Typography>
                {n.body && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {n.body.length > 80 ? n.body.slice(0, 80) + '...' : n.body}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </Box>
      </Popover>

      {/* User Menu */}
      {user && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {!isMobile && (
            <Typography
              variant="body2"
              sx={{
                color: '#4A3728',
                fontSize: '0.85rem',
                maxWidth: 120,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.display_name || user.email}
            </Typography>
          )}
          <IconButton size="small" onClick={handleMenuOpen} aria-label="Account menu" sx={{ color: '#7A5F4E' }}>
            <AccountCircle />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                backgroundColor: '#FFFFFF',
                border: '1px solid #F0E4CE',
                minWidth: 180,
                boxShadow: '0 4px 12px rgba(74,55,40,0.1)',
              },
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {user.display_name || 'User'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#7A5F4E' }}>
                {user.email}
              </Typography>
              {user.is_founding_member && (
                <Chip
                  label="Founding Member"
                  icon={<Star sx={{ fontSize: 14 }} />}
                  size="small"
                  sx={{ mt: 0.5, height: 22, fontSize: 11, backgroundColor: '#D4AF37', color: '#fff', fontWeight: 600, '& .MuiChip-icon': { color: '#fff' } }}
                />
              )}
            </Box>
            <Divider sx={{ borderColor: '#F0E4CE' }} />
            <MenuItem onClick={handleSettings}>
              <ListItemIcon><Settings fontSize="small" sx={{ color: '#7A5F4E' }} /></ListItemIcon>
              <ListItemText>Settings</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><Logout fontSize="small" sx={{ color: '#e74c3c' }} /></ListItemIcon>
              <ListItemText sx={{ '& .MuiTypography-root': { color: '#e74c3c' } }}>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      )}

      <EmergencyStopDialog
        open={showEmergencyStop}
        onClose={() => setShowEmergencyStop(false)}
        onConfirmed={() => { setShowEmergencyStop(false); window.location.reload(); }}
        openPositions={emergencyPositions}
        atRiskAmount={emergencyAtRisk}
      />

      {/* Kalshi connection gate */}
      <Dialog open={showKalshiDialog} onClose={() => setShowKalshiDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Connect Your Kalshi Account</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            To trade with real money, connect your Kalshi API key first. Your strategies will place orders through your own account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowKalshiDialog(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={() => { setShowKalshiDialog(false); navigate('/settings/kalshi'); }}>
            Connect Account
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Navbar;
